import { DownOutlined, DeleteOutlined } from '@ant-design/icons'
import { Collapse, Form, FormInstance } from 'antd'
import { useDiscountSchemas } from 'hooks/useDiscountSchemas'
import { DiscountSchema } from 'models/discount'
import { useState, useEffect } from 'react'
import { theme } from 'theme'
import { CreatePricingModalWrapper } from '../../../../atom/price'
import { PricingSchemaPayload } from '../../../../models/price'
import { Tenant } from '../../../../models/tenant'
import { DiscountFooterSectionModal } from './discount-footer-section'
import { InfoSectionModal } from './info-section'
import { NewDiscountSchemaSectionModal } from './new-discount-schema-section'
import React from 'react'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { ArchiveDiscountSchemaModal } from './archive-discount-schema-modal'

interface props {
  tenant: Tenant
  selectedDiscountSchema: DiscountSchema
  selectedPricingSchema: PricingSchemaPayload | undefined
  discounts: DiscountSchema[]
  discountId: number
  onCancel: () => void
  onOk: () => void
  goBack?: () => void
  index?: number
  lastIndex?: number
  formRef?: (form: FormInstance) => void
  forms?: FormInstance[]
  setMultiDiscountNameExists?: React.Dispatch<React.SetStateAction<{}>>
  setMultiPromoCodeExists?: React.Dispatch<React.SetStateAction<{}>>
  handleSubmitAllForms?: () => Promise<void>
  removeDiscountSchema?: (discountId: number) => void
}

const RenderNameSection: React.FC<{
  children: React.ReactNode
  lastIndex?: number
  discountId: number
  activeCollapsePanelKey: string
  onCollapseChange: (key: string | string[]) => void
  renderHeader: () => React.ReactNode
}> = ({ children, lastIndex, discountId, activeCollapsePanelKey, onCollapseChange, renderHeader }) => {
  return lastIndex && lastIndex > 1 ? (
    <div style={{ marginTop: 24 }}>
      <Collapse
        accordion
        activeKey={activeCollapsePanelKey}
        className="custom-collapse"
        onChange={onCollapseChange}
        style={{ borderRadius: '5px', backgroundColor: theme.colors.white }}
      >
        <Collapse.Panel showArrow={false} header={renderHeader()} className="custom-panel" key={discountId}>
          {children}
        </Collapse.Panel>
      </Collapse>
    </div>
  ) : (
    <>{children}</>
  )
}

const EditDiscountSchemaSection: React.FC<props> = ({
  tenant,
  selectedDiscountSchema,
  selectedPricingSchema,
  discounts,
  discountId,
  onCancel,
  onOk,
  goBack,
  index,
  lastIndex,
  formRef,
  forms = [],
  setMultiDiscountNameExists,
  setMultiPromoCodeExists,
  handleSubmitAllForms,
  removeDiscountSchema,
}) => {
  const [form] = Form.useForm()
  const {
    formName,
    initialValues,
    discountNameExists,
    promoCodeExists,
    loiteringType,
    timeOfDay,
    eventSchedules,
    timeOfDayLoitering,
    basePricingPlanRate,
    basePricingPlanLoiteringRate,
    weekdayRates,
    weekendRates,
    weekdayLoiteringRates,
    weekendLoiteringRates,
    setLoiteringType,
    setTimeOfDay,
    handleSelect,
    setPricePerUnitLoitering,
    setTimeOfDayLoitering,
    convertRate,
    validateInputChange,
    getDiscountNames,
    nameCheck,
    archiveDiscountSchema,
    onFinish,
    calculateDollarRateLimit,
  } = useDiscountSchemas(
    tenant,
    onOk,
    onCancel,
    selectedPricingSchema,
    discounts,
    selectedPricingSchema?.applied_listings,
    form,
    'edit',
    selectedDiscountSchema,
    index,
  )
  const [openArchiveDiscountSchemaModal, setOpenArchiveDiscountSchemaModal] = useState(false)
  const [activeCollapsePanelKey, setActiveCollapsePanelKey] = useState<string>('')
  const { discountPlanText } = useGeneralTranslation()

  useEffect(() => {
    if (formRef) {
      formRef(form)
    }
  }, [form, formRef])

  const handleCollapseChange = (key: string | string[]) => {
    setActiveCollapsePanelKey((prevKey) =>
      prevKey === (Array.isArray(key) ? key[0] : key) ? '' : Array.isArray(key) ? key[0] : key,
    )
  }
  const renderCollapseHeader = () => {
    return (
      <div className="custom-panel-header flex-panel-header body-01-regular">
        <div>{`${discountPlanText} ${index}`}</div>
        <span className={`custom-icon ${activeCollapsePanelKey === discountId.toString() ? 'rotate-icon' : ''}`}>
          <DownOutlined />
        </span>
        <span className="panel-delete-icon">
          <DeleteOutlined
            onClick={(e) => {
              e.stopPropagation()
              setOpenArchiveDiscountSchemaModal(true)
            }}
          />
        </span>
      </div>
    )
  }

  useEffect(() => {
    if (setMultiDiscountNameExists && setMultiPromoCodeExists) {
      setMultiDiscountNameExists((prev) => ({
        ...prev,
        [discountId]: Object.values(discountNameExists).some((state) => state === true) || false,
      }))
      setMultiPromoCodeExists((prev) => ({
        ...prev,
        [discountId]: Object.values(promoCodeExists).some((state) => state === true) || false,
      }))
    }
  }, [discountNameExists, promoCodeExists, discountId, setMultiDiscountNameExists, setMultiPromoCodeExists])

  return (
    <CreatePricingModalWrapper key={discountId}>
      <ArchiveDiscountSchemaModal
        visible={openArchiveDiscountSchemaModal}
        discountId={discountId}
        onOk={archiveDiscountSchema}
        onCancel={() => setOpenArchiveDiscountSchemaModal(false)}
        onFinish={() => removeDiscountSchema?.(discountId)}
      />
      <Form
        colon={false}
        form={form}
        layout="horizontal"
        requiredMark={true}
        labelAlign="left"
        initialValues={initialValues}
        onValuesChange={() => {
          if (formRef) {
            formRef(form)
          }
        }}
        onFinish={(values) => {
          if (Object.keys(values).length > 0) {
            onFinish(Object.keys(values).length > 0 ? values : initialValues, 'editDiscountSchema', discountId)
          } else {
            onOk()
          }
        }}
        labelWrap={true}
      >
        {(index === 1 || index === undefined) && (
          <InfoSectionModal
            tenant={tenant}
            listings={selectedPricingSchema?.applied_listings}
            pricingSchemaValues={selectedPricingSchema}
          />
        )}
        <RenderNameSection
          lastIndex={lastIndex}
          discountId={discountId}
          activeCollapsePanelKey={activeCollapsePanelKey}
          onCollapseChange={handleCollapseChange}
          renderHeader={renderCollapseHeader}
        >
          <NewDiscountSchemaSectionModal
            initialValues={initialValues}
            tenant={tenant}
            form={form}
            formName={formName}
            discountNameExists={discountNameExists}
            promoCodeExists={promoCodeExists}
            pricingSchemaValues={selectedPricingSchema}
            timeOfDay={timeOfDay}
            eventSchedules={eventSchedules}
            timeOfDayLoitering={timeOfDayLoitering}
            loiteringType={loiteringType}
            modalType="edit"
            basePricingPlanRate={basePricingPlanRate}
            basePricingPlanLoiteringRate={basePricingPlanLoiteringRate}
            weekdayRates={weekdayRates}
            weekendRates={weekendRates}
            weekdayLoiteringRates={weekdayLoiteringRates}
            weekendLoiteringRates={weekendLoiteringRates}
            nameCheck={nameCheck}
            getDiscountNames={getDiscountNames}
            onCancel={onCancel}
            setTimeOfDay={setTimeOfDay}
            setLoiteringType={setLoiteringType}
            setPricePerUnitLoitering={setPricePerUnitLoitering}
            setTimeOfDayLoitering={setTimeOfDayLoitering}
            handleSelect={handleSelect}
            convertRate={convertRate}
            validateInputChange={validateInputChange}
            calculateDollarRateLimit={calculateDollarRateLimit}
            back={goBack}
            hideFooter={!!index}
            index={index}
          />
        </RenderNameSection>
        {!!index && !!lastIndex && lastIndex === 1 && (
          <DiscountFooterSectionModal
            loading={false}
            form={form}
            formName={formName}
            discountNameExists={discountNameExists}
            promoCodeExists={promoCodeExists}
            current={1}
            modalType={goBack ? 'discount-only' : 'edit'}
            onCancel={onCancel}
            back={goBack ? goBack : () => {}}
            forms={forms}
            handleSubmitAllForms={handleSubmitAllForms}
          />
        )}
      </Form>
    </CreatePricingModalWrapper>
  )
}

const arePropsEqual = (prevProps: props, nextProps: props) => {
  // Check if only setMultiDiscountNameExists or setMultiPromoCodeExists changed
  const setterFunctionsChanged =
    prevProps.setMultiDiscountNameExists !== nextProps.setMultiDiscountNameExists ||
    prevProps.setMultiPromoCodeExists !== nextProps.setMultiPromoCodeExists

  if (setterFunctionsChanged) {
    // If only setter functions changed, don't re-render
    return true
  }

  // For all other prop changes, do a deep comparison
  return JSON.stringify(prevProps) === JSON.stringify(nextProps)
}
export default React.memo(EditDiscountSchemaSection, arePropsEqual)
